import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import {
  CheckCircle2,
  CreditCard,
  ArrowRight,
  Sparkles,
  Loader2,
  AlertTriangle,
  Home,
  Building2,
  User,
} from "lucide-react";

type SubscriptionStatus = "loading" | "success" | "processing" | "error";

/**
 * SubscriptionUpdated - Success page for subscription upgrades/downgrades
 * 
 * Route: /subscriptions/:subscriptionId/updated
 * Used when an existing subscription is upgraded or downgraded
 */
export function SubscriptionUpdated() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { data: session } = useSession();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<SubscriptionStatus>("loading");
  const [countdown, setCountdown] = useState(5);

  const sessionId = searchParams.get("session_id");

  // Get subscription details
  const { data: subscription, isLoading: subscriptionLoading } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! }
  );

  // Determine status based on whether we have a session_id (Checkout)
  useEffect(() => {
    if (!subscriptionLoading && subscription) {
      if (sessionId) {
        // Coming from Stripe Checkout
        const timer = setTimeout(() => {
          setStatus("success");
        }, 1000);
        return () => clearTimeout(timer);
      }

      // Downgrade or direct change - already completed
      setStatus("success");
    }
  }, [sessionId, subscription, subscriptionLoading]);

  // Countdown timer for auto-redirect
  useEffect(() => {
    if (status === "success" && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === "success" && countdown === 0) {
      // Redirect back to subscription page
      navigate(`/subscriptions/${subscriptionId}`);
    }
  }, [status, countdown, navigate, subscriptionId]);

  if (subscriptionLoading || status === "loading") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-neon-green mx-auto mb-4" />
          <p className="text-chromium-mist">Confirming your subscription...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card className="max-w-md w-full border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-silica mb-2">Something Went Wrong</h2>
              <p className="text-chromium-mist mb-6">
                We couldn't confirm your subscription. If you were charged, your subscription
                should be activated shortly. Please check your account or contact support.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate("/")}>
                  <Home className="h-4 w-4 mr-2" />
                  Go to Account
                </Button>
                <Button asChild className="bg-neon-green text-obsidian hover:bg-neon-green/90">
                  <a href="mailto:support@nosible.com">Contact Support</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentPlan = subscription?.plan.name || "free";
  const owner = subscription?.owner;
  const isOrganization = subscription?.isOrganization;

  return (
    <div className="w-full p-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
      <Card className="max-w-lg w-full border-neon-green/50">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 relative">
            <div className="absolute inset-0 bg-neon-green/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-neon-green/20 rounded-full p-4">
              <CheckCircle2 className="h-16 w-16 text-neon-green" />
            </div>
          </div>
          <CardTitle className="text-2xl text-silica">
            {sessionId ? "Payment Successful!" : "Plan Updated!"}
          </CardTitle>
          <CardDescription className="text-base">
            {sessionId
              ? "Your subscription has been upgraded"
              : "Your subscription has been changed"}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Owner Info */}
          {owner && (
            <div className="flex items-center justify-center gap-2 text-chromium-mist">
              {isOrganization ? (
                <Building2 className="h-4 w-4" />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span>{isOrganization ? "Organization" : "Personal"}: {owner.name}</span>
            </div>
          )}

          {/* Plan Details */}
          <div className="bg-slate-core/50 rounded-lg p-4 border border-slate-core">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-neon-green/20 rounded-lg">
                  <CreditCard className="h-5 w-5 text-neon-green" />
                </div>
                <div>
                  <p className="text-sm text-chromium-mist">Current Plan</p>
                  <p className="text-lg font-semibold text-silica capitalize">{currentPlan}</p>
                </div>
              </div>
              <Sparkles className="h-6 w-6 text-neon-green" />
            </div>
          </div>

          {/* What's Next */}
          <div className="space-y-3">
            <h3 className="font-medium text-silica">What's next?</h3>
            <ul className="space-y-2 text-sm text-chromium-mist">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>Your new plan features are now active</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>Create API keys to start using the Nosible API</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>View your usage and manage your subscription anytime</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button asChild className="w-full bg-neon-green text-obsidian hover:bg-neon-green/90">
              <Link to={`/subscriptions/${subscriptionId}`}>
                View Subscription
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <Button variant="outline" asChild className="w-full">
              <Link to={isOrganization && owner?.slug ? `/organizations/${owner.slug}` : "/"}>
                <Home className="h-4 w-4 mr-2" />
                {isOrganization ? "Go to Organization" : "Go to Account"}
              </Link>
            </Button>
          </div>

          {/* Auto-redirect notice */}
          <p className="text-xs text-chromium-mist/70 text-center">
            Redirecting to subscription in {countdown} seconds...
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
